import { Country, Currency } from "@lookiero/sty-psp-locale";
import { UUID } from "../../../infrastructure/ui/shared/types";
import { Price } from "../../projections/Price";
import type { Product } from "../product/Product";

enum ServiceStatus {
  CREATED = "CREATED",
  SELECTION_DONE = "SELECTION_DONE",
  CANCELLED = "CANCELLED",
  WAREHOUSE_ERROR = "WAREHOUSE_ERROR",
  SENT = "SENT",
  DELIVERY_NOTIFIED = "DELIVERY_NOTIFIED",
  LOST_BY_COURIER = "LOST_BY_COURIER",
  UNEXPECTED_RETURN = "UNEXPECTED_RETURN",
  DELIVERED = "DELIVERED",
  CLOSED = "CLOSED",
  DISCARDED = "DISCARDED",
}

enum OrderStatus {
  CREATED = "CREATED",
  IN_PROGRESS = "IN_PROGRESS",
  IDDLE = "IDDLE",
  CANCELLED = "CANCELLED",
  CLOSED = "CLOSED",
}

interface Promo {
  code: string;
  discountPercentage: number;
}

interface Answer {
  questionId: string;
  answerId: string;
}

interface Comment {
  questionId: string;
  content: string;
}

interface Feedback {
  answers: Answer[];
  comment: Comment | undefined;
}

interface ProductWithFeedback extends Product {
  feedback?: Feedback;
  itemId: string;
}

interface ItemToReturn {
  itemId: string;
  newStatus: string;
  feedback: Feedback;
}

class Order {
  bookingId?: UUID;
  countryCode: Country;
  checkoutId: string;
  currency: Currency;
  datePlanned: string;
  discardedItemsPrice?: number;
  initialPrice?: number;
  items: Product[];
  orderId: UUID;
  orderStatus?: string;
  pendingReturnItemsPrice?: number;
  price: number | Price; // TODO: depends on the type of request, the property price is an object { amount: number, currency: string } or an number.
  reference: string;
  returnedItemsPrice?: number;
  serviceId: string;
  serviceStatus?: string;
  totalPrice?: number;
  orginalTotalPrice?: number;
  promo: Promo | undefined;
  discountAmount: number;
  discountedPriceBySales: number;

  constructor({
    bookingId,
    countryCode,
    checkoutId,
    currency,
    datePlanned,
    discardedItemsPrice,
    initialPrice,
    items = [],
    orderId,
    orderStatus,
    pendingReturnItemsPrice,
    price = 0,
    reference,
    returnedItemsPrice,
    serviceId,
    serviceStatus,
    totalPrice,
    orginalTotalPrice,
    promo,
    discountAmount,
    discountedPriceBySales,
  }: {
    bookingId?: UUID;
    countryCode: Country;
    checkoutId: string;
    currency: Currency;
    datePlanned: string;
    discardedItemsPrice?: number;
    initialPrice?: number;
    items: Product[];
    orderId: UUID;
    orderStatus?: string;
    pendingReturnItemsPrice?: number;
    price: number;
    reference: string;
    returnedItemsPrice?: number;
    serviceId: string;
    serviceStatus?: string;
    totalPrice?: number;
    orginalTotalPrice?: number;
    promo: Promo | undefined;
    discountAmount: number;
    discountedPriceBySales: number;
  }) {
    this.bookingId = bookingId;
    this.countryCode = countryCode;
    this.checkoutId = checkoutId;
    this.currency = currency;
    this.datePlanned = datePlanned;
    this.discardedItemsPrice = discardedItemsPrice;
    this.initialPrice = initialPrice;
    this.items = items;
    this.orderId = orderId;
    this.orderStatus = orderStatus;
    this.pendingReturnItemsPrice = pendingReturnItemsPrice;
    this.price = price;
    this.reference = reference;
    this.returnedItemsPrice = returnedItemsPrice;
    this.serviceId = serviceId;
    this.serviceStatus = serviceStatus;
    this.totalPrice = totalPrice;
    this.orginalTotalPrice = orginalTotalPrice;
    this.promo = promo;
    this.discountAmount = discountAmount;
    this.discountedPriceBySales = discountedPriceBySales;
  }

  static calculateReturnPrice(order: Order): number {
    const returnedItemsPrice = order.returnedItemsPrice || 0;
    const pendingReturnItemsPrice = order.pendingReturnItemsPrice || 0;

    return returnedItemsPrice + pendingReturnItemsPrice;
  }
}

export type { Answer, Comment, Feedback, ItemToReturn, ProductWithFeedback, Promo };
export { Order, OrderStatus, ServiceStatus };
